// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import FancyScroll from "@guestsupply/App/Components/Common/FancyScroll";
import LazyTippy from "@guestsupply/App/Components/Common/LazyTippy";
import BrandsAndSolutions from "@guestsupply/App/Components/Header/BrandsAndSolutions";
import FeaturedProducts from "@guestsupply/App/Components/Header/FeaturedProducts";
import MarketingSection from "@guestsupply/App/Components/Header/MarketingSection";
import xHeaderUpdate from "@guestsupply/App/Handlers/XNG/Header";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { MarketingData } from "@guestsupply/Overrides/Components/MainNavigationItem";
import Cart from "@guestsupply/XDL/Icons/Cart";
import { buttonTransparent } from "@guestsupply/XDL/PreStyle/button";
import {
    addScrollbarGutter,
    color,
    ellipsis,
    removeScrollbarGutter,
    styledScrollbar,
} from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Icon from "@insite/mobius/Icon/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import parse from "html-react-parser";
import React, { RefObject } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";
import { hideAll, Instance } from "tippy.js";

const mapStateToProps = (state: CustomAppState) => ({
    headerHeight: state.xHeader?.headerHeight,
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    xHeaderUpdate,
};
const _menuWidth = 317;
// XNG-CHANGES : ADDING Menu interface
export interface MenuStyles {
    fancyScrollContainer: InjectableCss;
    parentLink: LinkPresentationProps;
    menuItemsWrapper: InjectableCss;
    brandsAndSoltionsWrapper: InjectableCss;
    solutionsTypography: TypographyProps;
}

export const menuStyles: MenuStyles = {
    fancyScrollContainer: {
        css: css`
            height: 100%;
            max-height: 100%;
        `,
    },
    parentLink: {
        color: "text.main",
        css: css`
            margin-bottom: 8px;
            margin-left: 9px;
            ${TypographyStyle} {
                font-weight: 500;
                line-height: 21px;
            }
        `,
    },
    menuItemsWrapper: {
        css: css`
            display: flex;
            span[direction="down"] {
                bottom: 1px;
            }
            span[direction="up"] {
                top: 1px;
            }
        `,
    },
    brandsAndSoltionsWrapper: {
        css: css`
            height: 496px;
            max-width: 1015px; // 1016px - 1px because of offset in tooltip
            margin-top: 1px;
            margin-left: -1px;
            border-radius: 0 0 4px 0;
        `,
    },
    solutionsTypography: {
        as: "p",
        css: css`
            color: ${getColor("text.main")};
            font-size: 14px;
            font-weight: 500;
            margin-left: 9px;
            margin-bottom: 12px;
        `,
    },
};
const styles = menuStyles;
export interface MappedLink {
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    // XNG-CHANGES
    detailPagePath?: string;
    featuredProducts?: string;
    content?: string;
    image?: string;
    onClick?: () => void;
}

export interface MenuPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
    url?: string;
    // xng-change: add google event
    handleClickLink: (title: string, url?: string) => void;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        // XNG-Changes
        trigger?: string;
        /** To show Menu from begging, x=0 */
        showAsFullWidth?: boolean;
        /** To prevent Menu close by click on trigger */
        hideOnClick?: boolean;
        /** To know which menu to show */
        pageType?: string;
        /** To show data for solutions and brands */
        marketingData?: MarketingData;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    MenuPresentationProps &
    ThemeProps<BaseTheme>;

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<
    ThemeProps<BaseTheme> &
        InjectableCss & {
            width?: number;
            headerHeight?: string;
        }
>`
    background: ${getColor("common.white")};
    width: ${getProp("width", 175)}px;
    height: 100%;
    max-height: calc((100vh - ${getProp("headerHeight", 140)}) * 0.8);
    padding: 16px 8px 8px;
    list-style: none;
    margin: 0;
    overflow: auto;
    border: 1px solid ${getColor("common.border")};
    border-radius: 0 0 4px 4px;
    ${styledScrollbar(0)}
    // XNG-Change: tippy must follow its parent height "if not its content height"
    div[data-tippy-root] {
        height: 100%;
        max-height: 496px;
        [class^="MenuStyle"] {
            border-radius: 0 0 4px 0;
        }
    }
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    color: ${getColor("text")};
    display: inline;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 16px;
    ${ellipsis(2)}
`;

// eslint-disable-next-line no-unexpected-multiline
const MenuItemStyle = styled.div<
    ThemeProps<BaseTheme> &
        InjectableCss & {
            width?: number;
        }
>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus {
        outline: none;
    }

    ${StyledButton} {
        display: inline-block;
        padding: 4px 8px;
        font-weight: 400;
        width: 100%;
        span {
            line-height: 21px;
            font-weight: 400;
            font-size: 14px !important; // added to override external in-line style
        }
    }
    ${IconWrapper} {
        margin-right: 4px;
        color: ${color("text.link")};
    }
    &:hover {
        color: ${color("common.ui9")};
        background-color: ${color("common.ui2")};
        box-shadow: inset 2px 0 0 0 ${color("common.ui9")};
        ${MenuItemText} {
            color: ${getColor("common.ui9")};
        }
    }
    &.active {
        &[aria-expanded="true"] {
            color: ${color("common.ui9")};
            background-color: ${color("common.ui2")};
            box-shadow: inset 2px 0 0 0 ${color("common.ui9")};

            & > ${StyledButton}, ${StyledButton}:hover, ${StyledButton}:focus {
                ${MenuItemText} {
                    color: ${color("common.ui9")};
                }
            }
        }
    }
    a${StyledButton}:hover > ${MenuItemText},a${StyledButton}:focus > ${MenuItemText} {
        color: ${getColor("common.ui9")};
    }

    ${injectCss}
`;

export const MenuTrigger = styled.span<InjectableCss<any>>`
    .trigger-item,
    .arrow {
        transition: transform ease-in-out 250ms;
        color: ${color("secondary.main")};
    }

    &:hover {
        .trigger-item {
            background: ${color("common.ui9")};
        }
        .arrow {
            color: ${color("common.white")};
        }
    }

    &[aria-expanded="true"] {
        .trigger-item {
            background-color: ${getColor("common.ui2")};
            box-shadow: inset 0 2px 0 0 ${getColor("common.ui9")};
            margin-top: -1px;
            padding-top: 17px;
            height: 51px;
            span {
                color: ${getColor("common.ui9")};
            }
            ${IconWrapper} {
                transform: rotate(180deg);
            }
        }
    }
`;

const hideOnPopperBlur = {
    name: "hideOnPopperBlur",
    defaultValue: true,
    fn(instance: Instance) {
        return {
            onCreate() {
                instance.popper.addEventListener("focusout", event => {
                    if (
                        event.relatedTarget &&
                        event.relatedTarget instanceof HTMLElement &&
                        !instance.popper.contains(event.relatedTarget)
                    ) {
                        instance.hide();
                    }
                });
            },
        };
    },
};

const ShowMenu = () => {
    return (
        <Button
            color="transparent"
            css={css`
                padding: 0;
                transform: translateY(-50%);
                min-height: initial;
                display: inline-flex;
                align-items: center;
                &,
                & ${IconWrapper} {
                    color: ${getColor("common.ui9")};
                }
                ${buttonTransparent}
            `}
            aria-label="Open Menu"
        >
            <IconMemo src={ChevronRight} size={15} />
            <VisuallyHidden>{translate("Open Menu")}</VisuallyHidden>
        </Button>
    );
};

interface MenuState {
    canBeOpen: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 *
 * You should add the code to Header page file:
 *   // Hide main navigation
 *   useEffect(() => {
 *       setTimeout(() => hideAll());
 *   }, [isSticky, currentLocation]);
 */
class Menu extends React.Component<MenuProps, MenuState> {
    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: _menuWidth,
        trigger: "mouseenter focusin",
        hideOnClick: false,
    };

    state: MenuState = {
        canBeOpen: true,
        shouldRender: true,
    };

    closeMenu = () => {
        this.setState({ canBeOpen: false }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    /**
     * XNG-Changes:
     * Remove ".active" class from all menuItems inside parentMenu depends on currentDepth
     * Then we add ".active" from onTrigger in LazyTippy if the menu has children o
     * @param currentDepth
     * @param parentMenu
     */
    clearMenuItemsActiveClass = (currentDepth: number, parentMenu: RefObject<HTMLUListElement>) => {
        if (typeof document === "undefined" || !parentMenu.current) {
            return;
        }

        parentMenu.current.querySelectorAll(`.MenuItem[data-depth="${currentDepth}"]`).forEach(item => {
            item.classList.remove("active");
        });
    };

    render() {
        const {
            showAsFullWidth,
            pageType,
            url,
            marketingData,
            // xng-change: add google event
            handleClickLink = () => {},
        } = this.props;
        // XNG-Changes
        const isBrandsPage = pageType === "BrandsPage";
        const isSolutionsPage = pageType === "SolutionsPage";
        const isBrandsPageOrSolutionsPage = isBrandsPage || isSolutionsPage;

        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, xHeaderUpdate, ...otherProps } = this.props;
                    const { applyProp, spreadProps } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );

                    const renderMenuItems = (
                        parentDepth: number,
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                        url?: string,
                        title?: string,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const parentMenu = React.createRef<HTMLUListElement>();
                        // XNG-CHANGES
                        const offset: [number, number] = !isBrandsPageOrSolutionsPage ? [0, -1] : [1, -1];
                        // XNG-CHANGES : Adding fancy scroll and structure for products, brands and solutions
                        return (
                            <FancyScroll
                                refElement={parentMenu}
                                showArrow={true}
                                scrollByValue={60}
                                extendedStyles={currentDepth > 1 ? styles.fancyScrollContainer : undefined}
                            >
                                <MenuStyle
                                    width={otherProps.width}
                                    css={cssOverrides?.menu}
                                    ref={parentMenu}
                                    className="MenuStyle"
                                    data-depth={currentDepth}
                                    {...{ headerHeight: this.props.headerHeight }}
                                >
                                    {isBrandsPage && (
                                        <Link
                                            href={url}
                                            onClick={() => {
                                                hideAll();
                                                // xng-change: add google event
                                                handleClickLink(translate("All Brands"), url);
                                            }}
                                            {...styles.parentLink}
                                            aria-label={translate("All Brands")}
                                        >
                                            {translate("All Brands")}
                                        </Link>
                                    )}
                                    {isSolutionsPage && (
                                        <Link
                                            href={url}
                                            onClick={() => {
                                                hideAll();
                                                // xng-change: add google event
                                                handleClickLink(translate("All Solutions"), url);
                                            }}
                                            {...styles.parentLink}
                                            aria-label={translate("All Solutions")}
                                        >
                                            {translate("All Solutions")}
                                        </Link>
                                    )}
                                    {!isBrandsPage && url && (
                                        <Link
                                            href={url}
                                            onClick={() => {
                                                hideAll();
                                                // xng-change: add google event
                                                handleClickLink(title!, url);
                                            }}
                                            {...styles.parentLink}
                                            aria-label={title}
                                        >
                                            {title}
                                        </Link>
                                    )}
                                    {childMenuItems
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, index) => {
                                            if (
                                                (item.type === "ChangeCustomerPage" &&
                                                    !this.props.displayChangeCustomerLink) ||
                                                !this.state.shouldRender
                                            ) {
                                                return null;
                                            }
                                            const maxDepth = item.maxDepth || parentDepth;
                                            const hasChildren =
                                                item.children?.some(link => !link.excludeFromNavigation) &&
                                                currentDepth < maxDepth;
                                            const menuItem = (
                                                <MenuItemStyle
                                                    width={otherProps.width}
                                                    css={cssOverrides?.menuItem}
                                                    className="MenuItem"
                                                    data-depth={currentDepth}
                                                    onClick={() => {
                                                        this.closeMenuWithoutChildren(
                                                            hasChildren || isBrandsPageOrSolutionsPage,
                                                        );
                                                    }}
                                                >
                                                    {!isBrandsPageOrSolutionsPage ? (
                                                        <>
                                                            <Clickable
                                                                href={!hasChildren ? item.url : ""}
                                                                onClick={() => {
                                                                    if (!hasChildren) {
                                                                        item.onClick?.();
                                                                        this.closeMenu();
                                                                        hideAll();
                                                                    }
                                                                    // xng-change: add google event
                                                                    handleClickLink(
                                                                        item.title,
                                                                        !hasChildren ? item.url : "",
                                                                    );
                                                                }}
                                                                {...applyProp("menuItemClickableProps")}
                                                                {...item.clickableProps}
                                                                aria-label={item.title}
                                                            >
                                                                {item.iconProps && <IconMemo {...item.iconProps} />}
                                                                <MenuItemText {...applyProp("menuItemTypographyProps")}>
                                                                    {item.title}
                                                                </MenuItemText>
                                                            </Clickable>
                                                            {hasChildren && <ShowMenu></ShowMenu>}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Clickable
                                                                {...applyProp("menuItemClickableProps")}
                                                                href={item?.detailPagePath || ""}
                                                                onClick={() => {
                                                                    if (item?.detailPagePath) {
                                                                        hideAll();
                                                                    }
                                                                    // xng-change: add google event
                                                                    handleClickLink(
                                                                        typeof item.title === "string"
                                                                            ? (item as any)?.brandName || item.title
                                                                            : parse(
                                                                                  (item as any)?.brandName ||
                                                                                      item.title,
                                                                              ),
                                                                        item?.detailPagePath,
                                                                    );
                                                                }}
                                                                {...item.clickableProps}
                                                                aria-label={
                                                                    typeof item.title === "string"
                                                                        ? item.title
                                                                        : parse(item.title)
                                                                }
                                                            >
                                                                {item.iconProps && <IconMemo {...item.iconProps} />}
                                                                <MenuItemText {...applyProp("menuItemTypographyProps")}>
                                                                    {typeof item.title === "string"
                                                                        ? (item as any)?.brandName || item.title
                                                                        : parse((item as any)?.brandName || item.title)}
                                                                </MenuItemText>
                                                            </Clickable>
                                                            <ShowMenu></ShowMenu>
                                                        </>
                                                    )}
                                                </MenuItemStyle>
                                            );

                                            return (
                                                <li key={`${item.title}_${index}`}>
                                                    {!hasChildren && !isBrandsPageOrSolutionsPage && menuItem}

                                                    {(hasChildren || isBrandsPageOrSolutionsPage) && (
                                                        <LazyTippy
                                                            render={() => {
                                                                if (
                                                                    isBrandsPageOrSolutionsPage &&
                                                                    !this.props.isTabletScreen
                                                                ) {
                                                                    return (
                                                                        <BrandsAndSolutions
                                                                            image={item.image || ""}
                                                                            content={item.content || ""}
                                                                            title={item.title}
                                                                            detailPagePath={item.detailPagePath || ""}
                                                                            featuredProducts={
                                                                                item.featuredProducts || ""
                                                                            }
                                                                        />
                                                                    );
                                                                }

                                                                return renderMenuItems(
                                                                    maxDepth,
                                                                    item!.children!,
                                                                    currentDepth + 1,
                                                                    item!.url,
                                                                    item!.title,
                                                                );
                                                            }}
                                                            trigger={
                                                                // Prodcuts onClick behaviour
                                                                isBrandsPageOrSolutionsPage ? "mouseenter" : "click"
                                                            }
                                                            placement="right-start"
                                                            interactive={true}
                                                            animation={false}
                                                            offset={offset}
                                                            delay={[100, 350]}
                                                            touch={item.url ? "hold" : true}
                                                            plugins={[hideOnPopperBlur]}
                                                            getReferenceClientRect={() => {
                                                                if (
                                                                    !parentMenu.current ||
                                                                    typeof parentMenu.current[
                                                                        "getBoundingClientRect"
                                                                    ] === "undefined"
                                                                ) {
                                                                    return new DOMRect(0, 0, 0, 0);
                                                                }
                                                                return parentMenu.current!.getBoundingClientRect();
                                                            }}
                                                            onTrigger={instance => {
                                                                this.clearMenuItemsActiveClass(
                                                                    currentDepth,
                                                                    parentMenu,
                                                                );
                                                                instance.reference.classList.add("active");
                                                            }}
                                                            popperOptions={{
                                                                modifiers: [
                                                                    {
                                                                        name: "preventOverflow",
                                                                        options: { mainAxis: false },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            {menuItem}
                                                        </LazyTippy>
                                                    )}
                                                </li>
                                            );
                                        })}
                                </MenuStyle>
                            </FancyScroll>
                        );
                    };
                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    const currentDepth = 1;
                    const appendToElement = showAsFullWidth
                        ? () => document.getElementById("main-navigation-container") || document.body
                        : "parent";

                    return (
                        <LazyTippy
                            render={() => (
                                <StyledWrapper {...styles.menuItemsWrapper}>
                                    {renderMenuItems(otherProps.maxDepth || 0, menuItems, currentDepth, url)}
                                    {/* To render the marketing data on first click on solutions and brands  */}
                                    {isBrandsPageOrSolutionsPage && (
                                        <StyledWrapper {...styles.brandsAndSoltionsWrapper}>
                                            {marketingData && !this.props.isTabletScreen && (
                                                <BrandsAndSolutions
                                                    image={marketingData?.marketingGeneralPromoImage}
                                                    content={marketingData.marketingGeneralPromoContent}
                                                    title={marketingData.marketingGeneralPromotionTitle}
                                                    detailPagePath=""
                                                    featuredProducts={marketingData.marketingFeaturedProducts}
                                                />
                                            )}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            )}
                            trigger="click"
                            placement="bottom-start"
                            hideOnClick={true}
                            interactive={true}
                            animation={false}
                            offset={[0, 0]}
                            plugins={[hideOnPopperBlur]}
                            appendTo={appendToElement}
                            onShow={instance => {
                                hideAll(); // To close all Tippy instances
                                xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_menu` });
                                addScrollbarGutter();
                                ariaAppHider.hide(document.body);
                            }}
                            onHidden={instance => {
                                xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_menu` });
                                removeScrollbarGutter();
                                ariaAppHider.show(document.body);
                            }}
                            popperOptions={{
                                modifiers: [
                                    {
                                        name: "arrow",
                                        options: {
                                            padding: 0,
                                            element: ".menu-arrow",
                                        },
                                    },
                                    {
                                        name: "computeStyles",
                                        options: {
                                            roundOffsets: ({ x, y }: any) => ({
                                                x: document
                                                    .getElementById("main-navigation-container")!
                                                    .getBoundingClientRect().left,
                                                y,
                                            }),
                                        },
                                    },
                                ],
                            }}
                        >
                            <MenuTrigger role="link">
                                {React.cloneElement(menuTrigger, {
                                    id: `${descriptionId}_${uniqueId()}`,
                                    className: `trigger trigger-item`,
                                })}
                            </MenuTrigger>
                        </LazyTippy>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(Menu));

export { MenuItemText };
